/**
 * Safe HTML sanitization utility using DOMPurify (lazy-loaded)
 *
 * DOMPurify is ~7kb gzipped, so we lazy-load it to avoid impacting initial bundle size.
 * Until DOMPurify loads, we use a basic HTML entity escape as fallback.
 */

type DOMPurifyType = {
  sanitize: (dirty: string) => string;
};

let domPurifyInstance: DOMPurifyType | null = null;
let loadPromise: Promise<DOMPurifyType> | null = null;
const loadCallbacks: Array<() => void> = [];

/**
 * Basic HTML entity escape as fallback before DOMPurify loads
 * This is NOT a full sanitizer - only escapes basic HTML entities
 */
function escapeHtmlEntities(html: string): string {
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Load DOMPurify lazily
 */
async function loadDOMPurify(): Promise<DOMPurifyType> {
  if (domPurifyInstance) {
    return domPurifyInstance;
  }

  loadPromise ??= import('dompurify').then((module) => {
    const instance = module.default as DOMPurifyType;
    domPurifyInstance = instance;
    // Notify all waiting callbacks
    loadCallbacks.forEach((callback) => callback());
    loadCallbacks.length = 0;
    return instance;
  });

  return loadPromise;
}

/**
 * Register a callback to be called when DOMPurify finishes loading
 * Returns an unsubscribe function
 */
export function onDOMPurifyLoad(callback: () => void): () => void {
  if (domPurifyInstance) {
    // Already loaded, call immediately
    callback();
    return () => {};
  }

  loadCallbacks.push(callback);
  // Start loading if not already
  void loadDOMPurify();

  return () => {
    const index = loadCallbacks.indexOf(callback);
    if (index > -1) {
      loadCallbacks.splice(index, 1);
    }
  };
}

/**
 * Sanitize HTML string using DOMPurify
 * Returns sanitized HTML or escaped HTML if DOMPurify hasn't loaded yet
 *
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) {
    return '';
  }

  if (domPurifyInstance) {
    return domPurifyInstance.sanitize(dirty);
  }

  // Fallback: escape HTML entities until DOMPurify loads
  // This is safe but won't render HTML - just shows escaped text
  return escapeHtmlEntities(dirty);
}

/**
 * Create a safe HTML object for React's dangerouslySetInnerHTML
 * Uses DOMPurify if loaded, otherwise escapes HTML entities
 *
 * @param dirty - The potentially unsafe HTML string
 * @returns Object with __html property for dangerouslySetInnerHTML
 */
export function createSafeHtml(dirty: string): { __html: string } {
  return { __html: sanitizeHtml(dirty) };
}

/**
 * Create a safe HTML object that uses DOMPurify when available
 * This version starts loading DOMPurify in the background
 *
 * @param dirty - The potentially unsafe HTML string
 * @returns Object with __html property for dangerouslySetInnerHTML
 */
export function createSafeHtmlLazy(dirty: string): { __html: string } {
  // Start loading DOMPurify in background (fire and forget)
  void loadDOMPurify();

  return createSafeHtml(dirty);
}

/**
 * Async version that waits for DOMPurify to load
 * Use this when you need guaranteed sanitization
 *
 * @param dirty - The potentially unsafe HTML string
 * @returns Promise resolving to sanitized HTML string
 */
export async function sanitizeHtmlAsync(dirty: string): Promise<string> {
  if (!dirty) {
    return '';
  }

  const purify = await loadDOMPurify();
  return purify.sanitize(dirty);
}

/**
 * Check if DOMPurify is loaded and ready
 */
export function isDOMPurifyReady(): boolean {
  return domPurifyInstance !== null;
}

// Pre-load DOMPurify after initial render (non-blocking)
if (typeof window !== 'undefined') {
  // Use requestIdleCallback for non-critical loading
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => void loadDOMPurify(), { timeout: 3000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => void loadDOMPurify(), 1000);
  }
}
